import { sortBy, keyBy, difference } from 'lodash'
import { musicApi } from 'api'

import {
  SET_PLAY_STATE,
  SET_DEMO_MODE,
  SET_PLAYED_TIME,
  RESET_PAIN_LEVELS_AND_SESSION_ID,
  RESET_SONG_INFO,
  SET_IMMERSIVE_VIDEO,
  SET_SONG_INFO,
  SET_INDICATION_TYPE,
  SET_SONG_ID,
  SET_SESSION_ID,
  SET_ALL_SONGS,
  SET_ALL_GENRES,
  SET_SONG_SESSION_INFO,
  START_FETCH_GENRES,
  FAIL_FETCH_MUSIC,
  ADD_TO_PLAYLIST,
  REMOVE_FROM_PLAYLIST,
  SET_PLAYLIST,
  PLAY_PLAYLIST,
  REORDER_PLAYLIST,
  SET_END_EVENT,
  START_FETCH_FROM_REMOTE,
  SET_ALL_LOCAL_GENRES,
  SET_ALL_LOCAL_SONGS,
  SET_VOLUME,
  SET_SESSION_ONLINE,
  SET_PAIN_BEFORE_LEVEL,
} from './actionTypes'

import { ANXIETY } from 'utils/constants'

import { saveGenresToStore, getGenresFromStore, saveSongInfoToStore } from 'utils/storage'
import { getNativeLocalPath } from 'utils/conversion'

import {
  addMissingOfflineSongInfo,
  downloadMissingGenreImages,
  downloadMissingSongImages,
} from './downloadActions'

// TODO: Temp placeholder images until save images
import GenericGenreImg from 'assets/genres/genre.png'
import GenericSongImg from 'assets/covers_img/cover_no-connection.jpg'

export const startPlaylist = () => (dispatch, getState) => {
  const { playlist, allSongs } = getState().player
  if (!playlist.length || !allSongs.length) return
  const songId = playlist[0]

  const songSessionInfo = {
    // Playlists always launch with session type ANXIETY
    indicationType: ANXIETY,
    songId,
  }
  dispatch({ type: PLAY_PLAYLIST, payload: 0 })
  dispatch(launchPlayer(songSessionInfo))
}

export const setPlayedTime = (time) => (dispatch) => {
  dispatch({ type: SET_PLAYED_TIME, payload: time })
}

export const playPlaylistNext = () => (dispatch, getState) => {
  const { playlist, allSongs, playingPlaylistSong } = getState().player

  if (!playlist.length || !allSongs.length) return

  const nextPlaylistIndex = playingPlaylistSong + 1

  // If finished playing last song in playlist, don't play next song
  if (playlist.length <= nextPlaylistIndex) return endPlaylistPlay()

  const songSessionInfo = {
    // Playlists always launch with session type ANXIETY
    indicationType: ANXIETY,
    songId: playlist[nextPlaylistIndex],
  }

  dispatch({
    type: PLAY_PLAYLIST,
    payload: nextPlaylistIndex,
  })
  dispatch(launchPlayer(songSessionInfo))
}

export const endPlaylistPlay = () => (dispatch) => {
  dispatch({
    type: PLAY_PLAYLIST,
    payload: null,
  })
}

export const addToPlaylist = (songId) => (dispatch, getState) => {
  const playlistItem = `playlist_${getState().auth.userId}`
  const playlist = JSON.parse(localStorage.getItem(playlistItem)) || []

  const playlistSet = new Set(playlist)
  playlistSet.add(songId)

  localStorage.setItem(playlistItem, JSON.stringify([...playlistSet]))
  dispatch({ type: ADD_TO_PLAYLIST, payload: songId })
}

export const removeFromPlaylist = (songId) => (dispatch, getState) => {
  const playlistItem = `playlist_${getState().auth.userId}`
  const playlist = JSON.parse(localStorage.getItem(playlistItem)) || []

  const playlistSet = new Set(playlist)
  playlistSet.delete(songId)

  localStorage.setItem(playlistItem, JSON.stringify([...playlistSet]))
  dispatch({ type: REMOVE_FROM_PLAYLIST, payload: songId })
}

export const reorderPlaylist = (firstIndex, secondIndex) => (dispatch, getState) => {
  const playlistItem = `playlist_${getState().auth.userId}`
  const playlist = JSON.parse(localStorage.getItem(playlistItem)) || []

  const reorderedP = [...playlist]
  ;[reorderedP[firstIndex], reorderedP[secondIndex]] = [
    reorderedP[secondIndex],
    reorderedP[firstIndex],
  ]

  localStorage.setItem(playlistItem, JSON.stringify([...reorderedP]))
  dispatch({ type: REORDER_PLAYLIST, payload: { firstIndex, secondIndex } })
}

export const initializePlaylist = () => (dispatch, getState) => {
  const playlistItem = `playlist_${getState().auth.userId}`
  const playlist = JSON.parse(localStorage.getItem(playlistItem)) || []

  dispatch({ type: SET_PLAYLIST, payload: playlist })
}

export const setPlayerInfo = (songSessionInfo) => (dispatch, getState) => {
  dispatch({ type: RESET_PAIN_LEVELS_AND_SESSION_ID })
  dispatch({ type: RESET_SONG_INFO })
  const { indicationType, songId } = songSessionInfo

  return dispatch({ type: SET_SONG_SESSION_INFO, payload: { indicationType, songId } })
}

export const setPlayState = (playStateBool) => (dispatch) => {
  return dispatch({ type: SET_PLAY_STATE, payload: playStateBool })
}

export const setVolume = (volume) => (dispatch) => {
  return dispatch({ type: SET_VOLUME, payload: volume })
}

export const setDemoMode = (demoModeBool) => (dispatch) => {
  return dispatch({ type: SET_DEMO_MODE, payload: demoModeBool })
}

export const launchPlayer = (songSessionInfo) => (dispatch) => {
  dispatch(setPlayerInfo(songSessionInfo))
}

export const setImmersiveVideo = (videoUrl) => (dispatch) =>
  dispatch({ type: SET_IMMERSIVE_VIDEO, payload: videoUrl })

export const setSongInfo = (songInfo) => (dispatch) =>
  dispatch({ payload: songInfo, type: SET_SONG_INFO })

export const setIndicationType =
  (selectedIndicationType = null) =>
  (dispatch) =>
    dispatch({ type: SET_INDICATION_TYPE, payload: selectedIndicationType })

export const resetPainLevelsAndSessionId = () => (dispatch) =>
  dispatch({ type: RESET_PAIN_LEVELS_AND_SESSION_ID })

export const resetSongInfo = () => (dispatch) => dispatch({ type: RESET_SONG_INFO })

export const setSongId = (selectedSongId) => (dispatch) =>
  dispatch({ type: SET_SONG_ID, payload: selectedSongId })

export const setSessionId = (sessionId) => (dispatch) =>
  dispatch({ type: SET_SESSION_ID, payload: sessionId })

const getGenres = async (isOnline, dispatch, getState) => {
  if (!isOnline) {
    if (!window.cordova) return []

    const downloadedGenreImages = getState().download.downloadedGenreImages
    const downloadedGenreImagesMap = keyBy(downloadedGenreImages, 'id')
    const genresFromStore = await getGenresFromStore()

    return Promise.all(
      genresFromStore.map(async (fetchedGenre) => ({
        ...fetchedGenre,
        imageUrl: downloadedGenreImagesMap[fetchedGenre.id]
          ? await getNativeLocalPath(
              `songs/genre_${fetchedGenre.id}.${
                downloadedGenreImagesMap[fetchedGenre.id].ext
              }`
            )
          : GenericGenreImg,
      }))
    )
  }

  const {
    result: { genres: fetchedGenres },
    error: genresErr,
  } = await musicApi.getGenres()

  if (window.cordova) {
    const downloadedGenreImages = getState().download.downloadedGenreImages

    if (downloadedGenreImages.length !== fetchedGenres.length) {
      const downloadedGenreImagesMap = keyBy(downloadedGenreImages, 'id')
      const missingGenres = fetchedGenres.filter(
        ({ id }) => !downloadedGenreImagesMap[id]
      )

      await dispatch(downloadMissingGenreImages(missingGenres))
    }
  }

  if (genresErr) throw Error(genresErr)

  await saveGenresToStore(fetchedGenres)

  return fetchedGenres
}

export const fetchAllMusic = () => async (dispatch, getState) => {
  if (!getState().auth.isAuthenticated) return

  if (window?.cordova)
    dispatch({
      type: START_FETCH_FROM_REMOTE,
      payload: { isFetchingSongsFromRemote: true },
    })

  const language = getState().auth.language.toLowerCase()

  dispatch({ type: START_FETCH_GENRES })
  try {
    const isOnline = getState().auth.isOnline
    const fetchedGenres = await getGenres(isOnline, dispatch, getState)

    const parsedGenres = sortBy(
      fetchedGenres.map(({ id, position, genreNameEn, genreNameFr, imageUrl }) => ({
        genreId: id,
        genreName: language === 'fr' ? genreNameFr : genreNameEn,
        genreImageUrl: imageUrl,
        position,
      })),
      'position'
    )

    const genresMap = keyBy(parsedGenres, 'genreId')

    dispatch({ type: SET_ALL_GENRES, payload: genresMap })

    // const fetchedSongs = isOnline
    //   ? await getRemoteSongs()
    //   : window.cordova
    //   ? getState().download.downloadedRawSongsInfo
    //   : []

    let fetchedSongs

    if (isOnline) fetchedSongs = await getRemoteSongs()

    if (!isOnline && window?.cordova)
      fetchedSongs = getState().download?.downloadedRawSongsInfo || []

    // return getNativeLocalPath(`songs/image_${id}.${ext}`)
    const fetchedSongsMap = keyBy(fetchedSongs, 'id')
    const parsedSongs = parseSongs(fetchedSongs, genresMap, language)
    const sortedSongs = sortBy(parsedSongs, 'position')
    const allSongsMap = keyBy(sortedSongs, 'songId')

    if (window.cordova && isOnline) {
      const { downloadedSongs, downloadedFilesIds, downloadedImages } =
        getState().download

      // TODO
      const missingSongImagesIds = difference(
        downloadedFilesIds,
        downloadedImages.map(({ id }) => id)
      )

      await dispatch(downloadMissingSongImages(missingSongImagesIds, fetchedSongsMap))

      const missingSongInfoIds = difference(downloadedFilesIds, downloadedSongs)
      const missingSongInfoIdsWithAvailableInfo = missingSongInfoIds.filter(
        (songId) => allSongsMap[songId]
      )

      if (missingSongInfoIdsWithAvailableInfo.length) {
        const addedRawSongInfo = await Promise.all(
          missingSongInfoIdsWithAvailableInfo.map((songId) =>
            saveSongInfoToStore(fetchedSongsMap[songId])
          )
        )

        const downloadedImagesMap = keyBy(getState().download.downloadedImages, 'id')

        const addedRawSongInfoWithImages = await Promise.all(
          addedRawSongInfo.map(async (rawSongInfo) => ({
            ...rawSongInfo,
            imageUrl: downloadedImagesMap[rawSongInfo.id]
              ? await getNativeLocalPath(
                  `songs/image_${rawSongInfo.id}.${
                    downloadedImagesMap[rawSongInfo.id].ext
                  }`
                )
              : GenericSongImg,
          }))
        )

        dispatch(
          addMissingOfflineSongInfo(
            missingSongInfoIdsWithAvailableInfo,
            addedRawSongInfoWithImages
          )
        )
      }
    }

    dispatch({
      type: SET_ALL_SONGS,
      payload: {
        allSongs: sortedSongs,
        allSongsMap,
        allRawSongsMap: isOnline ? fetchedSongsMap : {},
        isFetchingSongsFromRemote: false,
      },
    })
  } catch (err) {
    console.log('Error fetching music: ', err)
    console.log(JSON.stringify(err.message || JSON.stringify(err)))
    dispatch({ type: FAIL_FETCH_MUSIC, payload: err })
  }
}

export const fetchAllLocalMusic = () => async (dispatch, getState) => {
  if (!getState().auth.isAuthenticated) return

  if (!window?.cordova && getState().download.downloadedRawSongsInfo.length === 0) return

  const language = getState().auth.language.toLowerCase()

  try {
    // const isOnline = getState().auth.isOnline
    const fetchedGenres = await getGenres(false, dispatch, getState)

    const parsedGenres = sortBy(
      fetchedGenres.map(({ id, position, genreNameEn, genreNameFr, imageUrl }) => ({
        genreId: id,
        genreName: language === 'fr' ? genreNameFr : genreNameEn,
        genreImageUrl: imageUrl,
        position,
      })),
      'position'
    )

    const genresMap = keyBy(parsedGenres, 'genreId')

    dispatch({ type: SET_ALL_LOCAL_GENRES, payload: genresMap })

    // const fetchedSongs = isOnline
    //   ? await getRemoteSongs()
    //   : window.cordova
    //   ? getState().download.downloadedRawSongsInfo
    //   : []

    const fetchedSongs = getState().download?.downloadedRawSongsInfo || []

    // return getNativeLocalPath(`songs/image_${id}.${ext}`)
    // const fetchedSongsMap = keyBy(fetchedSongs, 'id')
    const parsedSongs = parseSongs(fetchedSongs, genresMap, language)
    const sortedSongs = sortBy(parsedSongs, ['artist', 'title'])
    const allLocalSongsMap = keyBy(sortedSongs, 'songId')

    dispatch({
      type: SET_ALL_LOCAL_SONGS,
      payload: {
        allLocalSongs: sortedSongs,
        allLocalSongsMap,
        isFetchingSongsFromRemote: false,
      },
    })
  } catch (err) {
    console.log('Error fetching local music: ', err)
    console.log(JSON.stringify(err.message || JSON.stringify(err)))
    dispatch({ type: FAIL_FETCH_MUSIC, payload: err })
  }
}

export const setEndEvent = (endEvent) => (dispatch) => {
  dispatch({ type: SET_END_EVENT, payload: endEvent })
}

const getRemoteSongs = async () => {
  const {
    result: { songs: fetchedSongs },
    error,
  } = await musicApi.getSongs()

  if (error) throw Error(error)

  return fetchedSongs
}

export const setSessionOnline = (status) => (dispatch) =>
  dispatch({ type: SET_SESSION_ONLINE, payload: status })

export const parseSongs = (songs, genresMap, language) =>
  songs
    .filter(({ genreId }) => genresMap[genreId])
    .map((song) => ({
      artist: song.artist,
      genreId: song.genreId,
      genreIdTwo: song.genreIdTwo,
      genreIdThree: song.genreIdThree,
      genreName: genresMap[song.genreId]?.genreName,
      imageUrl: song.imageUrl,
      audioUrl: song.audioUrl,
      songId: song.id,
      title: language === 'fr' ? song.titleFr : song.titleEn,
      description: language === 'fr' ? song.descriptionFr : song.descriptionEn,
      instruments: language === 'fr' ? [...song.instrumentsFr] : [...song.instrumentsEn],
      newUntil: song.newUntil,
      length: song.songLength,
      position: song.position,
      time1: song.time1,
      time2: song.time2,
      time3: song.time3,
      updated: song.updatedAt,
    }))

export const setPainLevelBefore = (painLevelBefore) => (dispatch) =>
  dispatch({
    payload: painLevelBefore,
    type: SET_PAIN_BEFORE_LEVEL,
  })
